/**
 * Art-lab HUD stub for the LG-1 key frame (LG-1 asks for "vault, two figures, frame, grain, HUD stub"): name banners
 * with HP ribbons, the «Пыл» bar and three prayer tiles placed by the fight layout rects (F3's `fightLayout` when
 * injected). Debug-only DOM; the real battle HUD is P4's. Strings are debug placeholders (see the F2 hand-off).
 */
import type { PanelRect } from '../../shared';

export interface HudRects {
  panel: PanelRect;
  turnOrder?: PanelRect | null;
  banner?: PanelRect | null;
  fervor?: PanelRect | null;
  prayerTiles?: PanelRect[] | null;
  chronicle?: PanelRect | null;
}

const LAB_TEXT = {
  hero: 'Сэр Мурзик', foe: 'Улитень Медлительный', fervor: 'Пыл', line: 'Мурзик огрел Улитня треской по шлему.',
  prayers: ['Заступа', 'Щит веры', 'Гнев'],
  turn: 'Черёд',
} as const;

const CSS = `
.al-hud{position:absolute;inset:0;pointer-events:none;font-family:Alegreya,Georgia,serif;color:var(--mc-ink,#2B1D14)}
.al-hud *{box-sizing:border-box}
.al-banner{position:absolute;display:flex;justify-content:space-between;gap:10px}
.al-side{flex:1 1 0;display:flex;flex-direction:column;gap:5px;min-width:0}
.al-side.r{align-items:flex-end;text-align:right}
.al-name{font:700 15px/1.1 'Alegreya SC',Alegreya,Georgia,serif;letter-spacing:.03em;white-space:nowrap;overflow:hidden;text-overflow:ellipsis;max-width:100%}
.al-ribbon{position:relative;width:100%;height:16px;border:1.5px solid #2B1D14;border-radius:2px;background:#E6D3A3;overflow:hidden;box-shadow:0 1px 0 #CDB37E}
.al-ribbon>i{position:absolute;inset:0;transform-origin:left}
.al-side.r .al-ribbon>i{transform-origin:right}
.al-ribbon.lapis>i{background:linear-gradient(#2747A8,#1B2F7A 70%,#0F1C4D)}
.al-ribbon.verm>i{background:linear-gradient(#D9472E,#C8341E 70%,#A82A17)}
.al-ribbon>b{position:absolute;inset:0;display:flex;align-items:center;justify-content:center;font:700 12px/1 Alegreya,Georgia,serif;color:#F7F1E1;font-variant-numeric:tabular-nums;text-shadow:0 1px 0 #17110D}
.al-roundel{width:44px;height:44px;border-radius:50%;border:2.5px solid #D4A537;background:radial-gradient(circle at 40% 35%,#2747A8,#1B2F7A 60%,#0F1C4D);box-shadow:0 0 0 1.5px #2B1D14,0 2px 3px rgba(23,17,13,.35);flex:none}
.al-roundel.v{background:radial-gradient(circle at 40% 35%,#D9472E,#C8341E 60%,#A82A17)}
.al-row{display:flex;align-items:center;gap:8px;width:100%}
.al-side.r .al-row{flex-direction:row-reverse}
.al-fervor{position:absolute;display:flex;align-items:center;gap:8px}
.al-fervor span{font:700 13px/1 'Alegreya SC',Georgia,serif}
.al-fervor div{flex:1;height:12px;border:1.5px solid #2B1D14;border-radius:2px;background:#E6D3A3;position:relative;overflow:hidden}
.al-fervor div>i{position:absolute;left:0;top:0;bottom:0;width:47%;background:linear-gradient(90deg,#9C7424,#D4A537 40%,#F3D77A 55%,#D4A537 70%)}
.al-tile{position:absolute;border:2px solid #2B1D14;border-radius:4px;background:linear-gradient(#FBF5E4,#F1E4C3);box-shadow:inset 0 0 0 2px #D4A537,0 2px 4px rgba(23,17,13,.25);display:flex;flex-direction:column;align-items:center;justify-content:center;gap:4px}
.al-tile .ic{width:32px;height:32px;border-radius:50%;border:2px solid #9C7424;background:radial-gradient(circle at 40% 35%,#F3D77A,#D4A537 55%,#9C7424);box-shadow:0 0 0 1.5px #2B1D14}
.al-tile .bn{font:700 13px/1 'Alegreya SC',Georgia,serif;color:#A82A17;letter-spacing:.02em}
.al-chron{position:absolute;font:italic 16px/1.3 Alegreya,Georgia,serif;color:#2B1D14;padding:0 4px}
.al-turn{position:absolute;display:flex;align-items:center;gap:6px}
.al-turn span{font:700 13px/1 'Alegreya SC',Georgia,serif;color:#5A4632;margin-right:2px}
.al-turn i{width:30px;height:30px;border-radius:50%;border:2px solid #9C7424;box-shadow:0 0 0 1.5px #2B1D14;background:#1B2F7A}
.al-turn i.v{background:#C8341E}
.al-turn em{font-style:normal;color:#5A4632;font-size:14px}
.al-turn b{margin-left:auto;width:40px;height:40px;border-radius:50%;border:2px solid #2B1D14;background:#F1E4C3;box-shadow:inset 0 0 0 2px #C29A45}
.al-ref{position:absolute;border:2px solid #2B1D14;box-shadow:0 0 0 4px #D4A537,0 6px 14px rgba(23,17,13,.35);border-radius:2px;object-fit:contain;background:#F1E4C3}
.al-ref-cap{position:absolute;font:700 13px/1.2 'Alegreya SC',Georgia,serif;color:#5A4632;text-align:center}
`;

export function mountHud(host: HTMLElement, rects: HudRects, opts: { refSrc: string | null; refRect: PanelRect | null }): () => void {
  const style = document.createElement('style');
  style.textContent = CSS;
  host.appendChild(style);
  const root = document.createElement('div');
  root.className = 'al-hud';
  host.appendChild(root);
  const place = (el: HTMLElement, r: PanelRect): void => {
    el.style.left = `${r.x}px`; el.style.top = `${r.y}px`; el.style.width = `${r.w}px`; el.style.height = `${r.h}px`;
  };
  const banner = rects.banner ?? { x: rects.panel.x, y: Math.max(8, rects.panel.y - 96), w: rects.panel.w, h: 80 };
  const b = document.createElement('div');
  b.className = 'al-banner';
  place(b, { ...banner, h: Math.min(banner.h, rects.turnOrder ? banner.h - 48 : 72) });
  const side = (right: boolean, name: string, hp: number, max: number): HTMLElement => {
    const s = document.createElement('div');
    s.className = `al-side${right ? ' r' : ''}`;
    s.innerHTML = `<div class="al-row"><div class="al-roundel${right ? ' v' : ''}"></div><div class="al-name">${name}</div></div>`
      + `<div class="al-ribbon ${right ? 'verm' : 'lapis'}"><i style="transform:scaleX(${hp / max})"></i><b>${hp} / ${max}</b></div>`;
    return s;
  };
  b.append(side(false, LAB_TEXT.hero, 112, 154), side(true, LAB_TEXT.foe, 74, 154));
  root.appendChild(b);
  if (rects.turnOrder) {
    const t = document.createElement('div');
    t.className = 'al-turn';
    place(t, rects.turnOrder);
    t.innerHTML = `<span>${LAB_TEXT.turn}:</span><i></i><em>›</em><i class="v"></i><em>›</em><i></i><b></b><b style="margin-left:6px"></b>`;
    root.appendChild(t);
  }
  if (rects.chronicle) {
    const c = document.createElement('div');
    c.className = 'al-chron';
    place(c, rects.chronicle);
    c.textContent = LAB_TEXT.line;
    root.appendChild(c);
  }
  if (rects.fervor) {
    const f = document.createElement('div');
    f.className = 'al-fervor';
    place(f, rects.fervor);
    f.innerHTML = `<span>${LAB_TEXT.fervor}</span><div><i></i></div><span>47</span>`;
    root.appendChild(f);
  }
  for (const [i, t] of (rects.prayerTiles ?? []).entries()) {
    const el = document.createElement('div');
    el.className = 'al-tile';
    place(el, t);
    el.innerHTML = `<div class="ic"></div><div class="bn">${LAB_TEXT.prayers[i] ?? ''}</div>`;
    root.appendChild(el);
  }
  if (opts.refSrc && opts.refRect) {
    const img = document.createElement('img');
    img.className = 'al-ref';
    img.src = opts.refSrc;
    img.alt = 'keyart-arena';
    place(img, opts.refRect);
    root.appendChild(img);
  }
  return () => { root.remove(); style.remove(); };
}
